import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Email as EmailIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  SmartToy as AiIcon,
  School as SchoolIcon,
  Favorite as HealthIcon,
  DirectionsRun as ActivitiesIcon,
  AccountBalance as FinancialIcon,
  Gavel as LegalIcon,
  People as SocialIcon,
  Folder as OtherIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  SaveAlt as SaveIcon,
} from '@mui/icons-material';
import { PageHeader, EmptyState, LoadingSpinner } from '../../components/common';
import { useQuery } from '@apollo/client/react';
import { GET_FAMILY, GET_FAMILY_EMAILS } from '@common/graphql/queries';
import { useSnackbar } from 'notistack';
// useFamily removed — emails carry their own family_id
import { insertFamilyData } from '../../services/dataApi';

const ACCENT = '#7C3AED';

type EmailCategory =
  | 'school'
  | 'health'
  | 'activities'
  | 'financial'
  | 'legal'
  | 'social'
  | 'other';

interface EmailData {
  from_address: string;
  from_name?: string;
  to_addresses?: string[];
  subject: string;
  body_text?: string;
  body_html?: string;
  received_at: string;
  category?: EmailCategory;
  associated_person_ids?: string[];
  summary?: string;
}

const CATEGORY_CONFIG: Record<
  EmailCategory,
  { label: string; icon: React.ElementType; color: string }
> = {
  school: { label: 'School', icon: SchoolIcon, color: '#EDA745' },
  health: { label: 'Health', icon: HealthIcon, color: '#DC5D72' },
  activities: { label: 'Activities', icon: ActivitiesIcon, color: '#48CFAD' },
  financial: { label: 'Financial', icon: FinancialIcon, color: '#A0BC60' },
  legal: { label: 'Legal', icon: LegalIcon, color: '#5D9CEC' },
  social: { label: 'Social', icon: SocialIcon, color: '#AC8EB8' },
  other: { label: 'Other', icon: OtherIcon, color: '#9CA3AF' },
};

const CATEGORY_ORDER: EmailCategory[] = [
  'school',
  'health',
  'activities',
  'financial',
  'legal',
  'social',
  'other',
];

const MailPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  // No currentFamilyId needed — emails have their own family_id
  const [selectedCategory, setSelectedCategory] = useState<EmailCategory | 'all'>('all');
  const [searchText, setSearchText] = useState('');
  const [selectedEmail, setSelectedEmail] = useState<any | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [savingAttachmentId, setSavingAttachmentId] = useState<string | null>(null);

  const { data: emailData, loading, refetch } = useQuery(GET_FAMILY_EMAILS, {
    fetchPolicy: 'cache-and-network',
  });
  const { data: familyData } = useQuery(GET_FAMILY);

  const familyMembers = useMemo(() => {
    const families = (familyData as any)?.family || [];
    const members = families[0]?.members || [];
    return members.reduce((acc: Record<string, string>, m: any) => {
      const name = `${m.person?.first_name || ''} ${m.person?.last_name || ''}`.trim();
      acc[m.person_id] = name || 'Member';
      return acc;
    }, {} as Record<string, string>);
  }, [familyData]);

  const emails = useMemo(() => {
    const rawEmails = (emailData as any)?.email || [];
    return rawEmails.map((e: any) => ({
      id: e.id,
      data: {
        from_address: e.from_address,
        from_name: e.data?.from_name,
        to_addresses: e.to_address ? [e.to_address] : [],
        subject: e.subject,
        body_text: e.body_text,
        body_html: e.body_html,
        received_at: e.received_at,
        category: e.category,
        associated_person_ids: e.email_people?.map((p: any) => p.person_id) || [],
        summary: e.data?.summary,
      } as EmailData,
      created_at: e.created_at,
    }));
  }, [emailData]);

  const filteredEmails = useMemo(() => {
    let result = emails;

    if (selectedCategory !== 'all') {
      result = result.filter((e: any) => e.data?.category === selectedCategory);
    }

    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (e: any) =>
          e.data?.subject?.toLowerCase().includes(lower) ||
          e.data?.from_address?.toLowerCase().includes(lower) ||
          e.data?.from_name?.toLowerCase().includes(lower)
      );
    }

    return result;
  }, [emails, selectedCategory, searchText]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: emails.length };
    for (const cat of CATEGORY_ORDER) {
      counts[cat] = emails.filter((e: any) => e.data?.category === cat).length;
    }
    return counts;
  }, [emails]);

  const todayEmails = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return emails.filter((e: any) => {
      if (!e.data?.received_at) return false;
      return new Date(e.data.received_at) >= today;
    });
  }, [emails]);

  const todayByCategory = useMemo(() => {
    const grouped: Partial<Record<EmailCategory, typeof todayEmails>> = {};
    for (const email of todayEmails) {
      const cat: EmailCategory = email.data?.category || 'other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat]!.push(email);
    }
    return grouped;
  }, [todayEmails]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isThisYear = date.getFullYear() === now.getFullYear();

    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    if (isThisYear) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatFullDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const handleEmailClick = (email: any) => {
    setSelectedEmail(email);
    setDetailOpen(true);
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
    setSelectedEmail(null);
  };

  const handleSaveToDocuments = useCallback(async (attachment: any) => {
    const emailFamilyId = selectedEmail?.family_id;
    if (!emailFamilyId) {
      enqueueSnackbar('Could not determine family for this email', { variant: 'warning' });
      return;
    }
    const attId = attachment.id || attachment.filename;
    setSavingAttachmentId(attId);
    try {
      await insertFamilyData(emailFamilyId, 'document', {
        name: attachment.filename || attachment.name || 'Email Attachment',
        category: 'other',
        source: 'email',
        email_id: selectedEmail?.id,
        email_attachment_id: attachment.id,
      });
      enqueueSnackbar('Saved to Documents', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to save to Documents', { variant: 'error' });
    } finally {
      setSavingAttachmentId(null);
    }
  }, [selectedEmail, enqueueSnackbar]);

  if (loading && emails.length === 0) {
    return <LoadingSpinner message="Loading emails..." />;
  }

  const selectedEmailData = selectedEmail?.data;
  const selectedCatConfig = selectedEmailData?.category
    ? CATEGORY_CONFIG[selectedEmailData.category as EmailCategory]
    : null;

  return (
    <Box>
      <PageHeader
        title="Family Mail"
        subtitle="View and organize family emails by category"
        secondaryAction={
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />

      {/* Daily Summary Card */}
      <Card sx={{ mb: 3, border: `1px solid ${ACCENT}30` }}>
        <CardHeader
          avatar={
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: ACCENT + '15',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AiIcon sx={{ color: ACCENT, fontSize: 22 }} />
            </Box>
          }
          title={
            <Typography variant="h6" fontWeight={700}>
              Today's Summary
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
              {' · '}
              {todayEmails.length} email{todayEmails.length !== 1 ? 's' : ''}
            </Typography>
          }
          action={
            <IconButton onClick={() => setSummaryExpanded((p) => !p)} size="small">
              {summaryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
          sx={{ pb: 1 }}
        />
        <Collapse in={summaryExpanded}>
          <CardContent sx={{ pt: 0 }}>
            {todayEmails.length === 0 ? (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No emails received today. You're all caught up!
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {CATEGORY_ORDER.filter((cat) => todayByCategory[cat]?.length).map((cat) => {
                  const config = CATEGORY_CONFIG[cat];
                  const Icon = config.icon;
                  const catEmails = todayByCategory[cat]!;

                  return (
                    <Box key={cat}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Icon sx={{ color: config.color, fontSize: 18 }} />
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: config.color,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          {config.label}
                        </Typography>
                        <Chip
                          label={catEmails.length}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            bgcolor: config.color + '20',
                            color: config.color,
                            fontWeight: 700,
                          }}
                        />
                      </Box>
                      <Box
                        component="ul"
                        sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}
                      >
                        {catEmails.map((email: any) => (
                          <Box
                            key={email.id}
                            component="li"
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleEmailClick(email)}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                '&:hover': { color: ACCENT, textDecoration: 'underline' },
                              }}
                            >
                              {email.data?.summary || email.data?.subject}
                            </Typography>
                            {email.data?.summary && (
                              <Typography variant="caption" color="text.disabled">
                                Re: {email.data?.subject}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Collapse>
      </Card>

      {/* Email List Card */}
      <Card>
        <CardContent>
          {/* Search */}
          <Box sx={{ mb: 2 }}>
            <TextField
              placeholder="Search by subject or sender..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              fullWidth
              sx={{ maxWidth: 400 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          {/* Category Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={selectedCategory}
              onChange={(_, val) => setSelectedCategory(val)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root.Mui-selected': { color: ACCENT },
                '& .MuiTabs-indicator': { backgroundColor: ACCENT },
              }}
            >
              <Tab label={`All (${categoryCounts.all})`} value="all" />
              {CATEGORY_ORDER.map((cat) => {
                const config = CATEGORY_CONFIG[cat];
                return (
                  <Tab
                    key={cat}
                    label={`${config.label} (${categoryCounts[cat] || 0})`}
                    value={cat}
                    disabled={categoryCounts[cat] === 0}
                  />
                );
              })}
            </Tabs>
          </Box>

          {/* Email List */}
          {filteredEmails.length === 0 ? (
            <EmptyState
              icon={EmailIcon}
              title={
                searchText || selectedCategory !== 'all' ? 'No emails found' : 'No emails yet'
              }
              description={
                !searchText && selectedCategory === 'all'
                  ? 'Family emails will appear here once received'
                  : undefined
              }
            />
          ) : (
            <List disablePadding>
              {filteredEmails.map((email: any, idx: number) => {
                const data = email.data;
                if (!data) return null;
                const catConfig = data.category
                  ? CATEGORY_CONFIG[data.category as EmailCategory]
                  : CATEGORY_CONFIG.other;
                const Icon = catConfig.icon;

                return (
                  <React.Fragment key={email.id}>
                    {idx > 0 && <Divider />}
                    <ListItemButton
                      onClick={() => handleEmailClick(email)}
                      sx={{ px: 0, py: 1.5, borderRadius: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 52 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: catConfig.color + '20',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Icon sx={{ color: catConfig.color, fontSize: 20 }} />
                        </Box>
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                              <Typography variant="body1" fontWeight={600} noWrap>
                                {data.from_name || data.from_address}
                              </Typography>
                              {data.category && (
                                <Chip
                                  label={catConfig.label}
                                  size="small"
                                  sx={{
                                    bgcolor: catConfig.color + '20',
                                    color: catConfig.color,
                                    fontWeight: 600,
                                    fontSize: '0.65rem',
                                    height: 20,
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{ flexShrink: 0 }}
                            >
                              {formatDate(data.received_at)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.primary" noWrap>
                              {data.subject}
                            </Typography>
                            {(data.summary || data.body_text) && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {data.summary || data.body_text?.replace(/\s+/g, ' ').trim()}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Email Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={handleDetailClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedEmailData && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pr: 4 }}>
                {selectedCatConfig && (
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: selectedCatConfig.color + '20',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {React.createElement(selectedCatConfig.icon, {
                      sx: { color: selectedCatConfig.color, fontSize: 24 },
                    })}
                  </Box>
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                    {selectedEmailData.subject}
                  </Typography>
                  {selectedCatConfig && (
                    <Chip
                      label={selectedCatConfig.label}
                      size="small"
                      sx={{
                        mt: 0.5,
                        bgcolor: selectedCatConfig.color + '20',
                        color: selectedCatConfig.color,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />
                  )}
                </Box>
              </Box>
              <IconButton
                onClick={handleDetailClose}
                size="small"
                sx={{ position: 'absolute', top: 12, right: 12 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers>
              {/* Metadata */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  mb: 2,
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" color="disabled" />
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
                    From
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedEmailData.from_name
                      ? `${selectedEmailData.from_name} <${selectedEmailData.from_address}>`
                      : selectedEmailData.from_address}
                  </Typography>
                </Box>

                {selectedEmailData.to_addresses?.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="disabled" />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
                      To
                    </Typography>
                    <Typography variant="body2">
                      {selectedEmailData.to_addresses.join(', ')}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimeIcon fontSize="small" color="disabled" />
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
                    Date
                  </Typography>
                  <Typography variant="body2">
                    {formatFullDate(selectedEmailData.received_at)}
                  </Typography>
                </Box>
              </Box>

              {/* Associated persons */}
              {selectedEmailData.associated_person_ids?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.secondary',
                      display: 'block',
                      mb: 1,
                    }}
                  >
                    Associated Family Members
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedEmailData.associated_person_ids.map((pid: string) => {
                      const name = familyMembers[pid] || 'Family Member';
                      return (
                        <Chip
                          key={pid}
                          label={name}
                          size="small"
                          sx={{
                            bgcolor: ACCENT + '15',
                            color: ACCENT,
                            fontWeight: 600,
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* AI Summary */}
              {selectedEmailData.summary && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: ACCENT + '08',
                    border: `1px solid ${ACCENT}25`,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <AiIcon sx={{ color: ACCENT, fontSize: 16 }} />
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{
                        color: ACCENT,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      AI Summary
                    </Typography>
                  </Box>
                  <Typography variant="body2">{selectedEmailData.summary}</Typography>
                </Paper>
              )}

              {/* Attachments */}
              {selectedEmailData.attachments?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', display: 'block', mb: 1 }}
                  >
                    Attachments ({selectedEmailData.attachments.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedEmailData.attachments.map((att: any, idx: number) => {
                      const fileName = att.filename || att.name || `Attachment ${idx + 1}`;
                      const fileSize = att.size ? (att.size < 1024 * 1024 ? `${(att.size / 1024).toFixed(1)} KB` : `${(att.size / (1024 * 1024)).toFixed(1)} MB`) : '';
                      const isPdf = fileName.toLowerCase().endsWith('.pdf');
                      const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);

                      const handleDownload = async () => {
                        try {
                          // Use the mail REST API to get presigned URL
                          const mailApiBase = (window as any).__familypal_api_url || '';
                          const mailUrl = mailApiBase.replace('/identity', '/mail');
                          const emailId = selectedEmail?.id;
                          if (!emailId) return;

                          const res = await fetch(`${mailUrl}/emails/${emailId}`, { credentials: 'include' });
                          if (!res.ok) throw new Error('Failed to fetch');
                          const emailData = await res.json();
                          const attachment = emailData.attachments?.find((a: any) => a.id === att.id);
                          const downloadUrl = attachment?.download_url || attachment?.downloadUrl;
                          if (downloadUrl) {
                            window.open(downloadUrl, '_blank');
                          }
                        } catch {
                          // Fallback: try download_url from stored data
                          if (att.download_url || att.downloadUrl) {
                            window.open(att.download_url || att.downloadUrl, '_blank');
                          }
                        }
                      };

                      return (
                        <Paper
                          key={att.id || idx}
                          variant="outlined"
                          sx={{
                            p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Box
                            sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: isPdf ? '#EF4444' + '20' : isImage ? '#3B82F6' + '20' : 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                            onClick={handleDownload}
                          >
                            <Typography variant="caption" fontWeight={700} sx={{ color: isPdf ? '#EF4444' : isImage ? '#3B82F6' : 'text.secondary', fontSize: '0.6rem' }}>
                              {isPdf ? 'PDF' : isImage ? 'IMG' : 'FILE'}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={handleDownload}>
                            <Typography variant="body2" fontWeight={500} noWrap>{fileName}</Typography>
                            {fileSize && <Typography variant="caption" color="text.secondary">{fileSize}</Typography>}
                          </Box>
                          <Tooltip title="Save to Documents">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleSaveToDocuments(att); }}
                              disabled={savingAttachmentId === (att.id || att.filename)}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Paper>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Email body */}
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    color: 'text.secondary',
                    display: 'block',
                    mb: 1,
                  }}
                >
                  Message
                </Typography>
                {selectedEmailData.body_text ? (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'background.default',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {selectedEmailData.body_text
                      .split(/\n{2,}/)
                      .map((para: string, idx: number) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          sx={{ mb: 1.5, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
                        >
                          {para.trim()}
                        </Typography>
                      ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    No message body available.
                  </Typography>
                )}
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleDetailClose} variant="outlined" sx={{ borderRadius: 2 }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default MailPage;
